import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import type { Appointment } from '../utils/appointmentManager';
import {
  formatBookingPaymentMethod,
  isBookingAwaitingPaymentConfirmation,
  isBookingPrepaid,
} from '../utils/appointmentPaymentDisplay';

export type AppointmentPrepaidBadgeVariant = 'pill' | 'banner';

type Props = {
  appointment: Pick<Appointment, 'paymentData'>;
  variant?: AppointmentPrepaidBadgeVariant;
};

/**
 * Shows when booking payment was completed at checkout (paymentData.status === 'completed'),
 * or — when the customer paid the business directly via off-platform PayPal that BookAPT
 * can't verify — an amber badge prompting the merchant to confirm the payment themselves.
 * Use `pill` in compact list rows; `banner` in expanded detail blocks.
 */
export function AppointmentPrepaidBadge({ appointment, variant = 'pill' }: Props) {
  if (isBookingAwaitingPaymentConfirmation(appointment)) {
    if (variant === 'banner') {
      return (
        <View style={styles.pendingBanner}>
          <Text style={styles.pendingBannerTitle}>⚠️ Confirm payment on PayPal</Text>
          <Text style={styles.pendingBannerSub}>
            ${Number(appointment.paymentData?.amount ?? 0).toFixed(2)} · Customer chose to pay you
            directly on PayPal. Check your PayPal account before treating this booking as paid.
          </Text>
        </View>
      );
    }
    return (
      <View style={styles.pendingPill}>
        <Text style={styles.pendingPillText}>Confirm payment</Text>
      </View>
    );
  }

  if (!isBookingPrepaid(appointment)) return null;

  if (variant === 'banner') {
    const tx = appointment.paymentData?.transactionId;
    const txTail = tx ? String(tx).slice(-8) : '';
    return (
      <View style={styles.banner}>
        <Text style={styles.bannerTitle}>
          Paid at booking · {formatBookingPaymentMethod(appointment.paymentData?.paymentMethod)}
        </Text>
        <Text style={styles.bannerSub}>
          ${Number(appointment.paymentData?.amount ?? 0).toFixed(2)}
          {txTail ? ` · Ref: …${txTail}` : ''}
        </Text>
        {appointment.paymentData?.demoFlow ? (
          <Text style={styles.bannerDemoNote}>
            Web demo: no real card was charged. Production will use Stripe wallet checkout.
          </Text>
        ) : null}
      </View>
    );
  }

  return (
    <View style={styles.pill}>
      <Text style={styles.pillText}>Paid · {formatBookingPaymentMethod(appointment.paymentData?.paymentMethod)}</Text>
    </View>
  );
}

const styles = StyleSheet.create({
  pill: {
    backgroundColor: '#D1FAE5',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#6EE7B7',
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
  pillText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#065F46',
  },
  banner: {
    backgroundColor: '#D1FAE5',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#6EE7B7',
  },
  bannerTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#065F46',
  },
  bannerSub: {
    fontSize: 13,
    color: '#047857',
    marginTop: 2,
  },
  bannerDemoNote: {
    fontSize: 11,
    color: '#047857',
    marginTop: 6,
    fontStyle: 'italic',
  },
  pendingPill: {
    backgroundColor: '#FEF3C7',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#FCD34D',
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
  pendingPillText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#92400E',
  },
  pendingBanner: {
    backgroundColor: '#FEF3C7',
    borderRadius: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#FCD34D',
  },
  pendingBannerTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#92400E',
  },
  pendingBannerSub: {
    fontSize: 13,
    color: '#92400E',
    marginTop: 2,
  },
});
